import { useSetAtom } from 'jotai';
import React, { useEffect } from 'react';
import { BodyMediumRegular, Spinner, View, YStack } from 'smoothie';

import TransactionDetails from '../../../banking/components/spaces/transactions/TransactionDetails';
import { selectedTransactionV2Atom } from '../../../banking/state/SpacesAtoms';
import type { BankAccountDisplayDetail } from '../../../banking/types/BankAccount';
import type { Transaction } from '../../../banking/types/Transactions';

interface EnrichedTransactionDetailsViewProps {
    account: BankAccountDisplayDetail | null;
    transaction: Transaction | null;
    isLoading: boolean;
    notFound: boolean;
    syncteraAccountId: string;
}

/**
 * Presentational view that renders the shared {@link TransactionDetails} for an
 * enriched transaction. Data fetching (which depends on AuthedUserProvider) must
 * happen in the caller's component body, since this view renders inside the
 * sheet portal which is mounted outside the app's provider tree.
 */
export const EnrichedTransactionDetailsView = ({
    account,
    transaction,
    isLoading,
    notFound,
    syncteraAccountId,
}: EnrichedTransactionDetailsViewProps) => {
    const setSelectedTransaction = useSetAtom(selectedTransactionV2Atom);

    useEffect(() => {
        if (transaction != null) {
            setSelectedTransaction({ transaction, syncteraAccountId });
        }
    }, [transaction, syncteraAccountId, setSelectedTransaction]);

    if (isLoading) {
        return (
            <View
                width="100%"
                paddingVertical={48}
                justifyContent="center"
                alignItems="center"
            >
                <Spinner />
            </View>
        );
    }

    if (notFound || transaction == null || account == null) {
        return (
            <YStack paddingVertical={48} alignItems="center">
                <BodyMediumRegular color="$gray1000" textAlign="center">
                    Transaction details aren&apos;t available right now.
                </BodyMediumRegular>
            </YStack>
        );
    }

    return <TransactionDetails account={account} />;
};

export default EnrichedTransactionDetailsView;
